import { useEffect, useState } from "react";
import { MessageCircleQuestion, MessageCircle, Bug, LifeBuoy, Lightbulb, X } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n.jsx";
import { openExternal, nativePlatform } from "@/lib/native";

const KEY = "calofay_checkin_last";
const EVERY_MS = 14 * 86400000;
const MIN_ACCOUNT_AGE_MS = 3 * 86400000;

// Every 2 weeks (first one right away), never in the account's first 3 days.
export function shouldShowSupportCheckIn(user) {
  try {
    if (!user?.created_at || Date.now() - new Date(user.created_at).getTime() < MIN_ACCOUNT_AGE_MS) return false;
    const last = Number(localStorage.getItem(KEY)) || 0;
    return Date.now() - last >= EVERY_MS;
  } catch { return false; }
}

const markShown = () => { try { localStorage.setItem(KEY, String(Date.now())); } catch {} };

const OPTIONS = [
  { key: "problem", Icon: Bug, ar: "تواجه مشكلة في التطبيق", en: "Facing a problem in the app" },
  { key: "help", Icon: LifeBuoy, ar: "تحتاج مساعدة أو دعم", en: "Need help or support" },
  { key: "idea", Icon: Lightbulb, ar: "عندك اقتراح أو ميزة تطويرية", en: "Have an idea or feature request" },
];

export default function SupportCheckIn({ open, onClose, user }) {
  const { lang } = useI18n();
  const en = lang === "en";
  const [num, setNum] = useState("");
  useEffect(() => {
    if (!open) return;
    markShown();
    api.get("/config/public").then(({ data }) => setNum(String(data?.whatsapp_number || "").replace(/\D/g, ""))).catch(() => {});
  }, [open]);
  if (!open) return null;

  const pick = () => {
    const text = `${en ? "Hi, I'd like to share:" : "مرحبا، حاب أشارككم:"}\n\n\n${en ? "My account" : "حسابي"}: ${user?.email || ""}\n${en ? "Device" : "الجهاز"}: ${nativePlatform()}`;
    if (num) openExternal(`https://wa.me/${num}?text=${encodeURIComponent(text)}`);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={onClose} data-testid="support-checkin-modal">
      <div onClick={(e) => e.stopPropagation()} dir={en ? "ltr" : "rtl"}
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl animate-in slide-in-from-bottom-4 fade-in duration-300"
        style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
        <div className="flex items-start justify-between gap-3">
          <div className="w-12 h-12 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
            <MessageCircleQuestion className="w-6 h-6" />
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-neutral-100 text-neutral-500 flex items-center justify-center" data-testid="support-checkin-close"><X className="w-4 h-4" /></button>
        </div>
        <h2 className="text-xl font-black mt-3">{en ? "How's Calofay going for you?" : "كيف تجربتك مع كالوفاي؟"}</h2>
        <p className="text-sm text-neutral-500 mt-1 leading-relaxed">
          {en ? "Facing an issue, need help, or have an idea? Message us directly — we read every message." : "تواجه مشكلة، تحتاج مساعدة، أو عندك فكرة تطوّر التطبيق؟ راسلنا مباشرة — نقرأ كل رسالة."}
        </p>
        <div className="mt-4 space-y-2.5">
          {OPTIONS.map((o) => (
            <div key={o.key} className="flex items-center gap-3 text-sm text-neutral-700">
              <div className="w-8 h-8 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center shrink-0"><o.Icon className="w-4 h-4" /></div>
              <span>{en ? o.en : o.ar}</span>
            </div>
          ))}
        </div>
        <div className="mt-6 space-y-2">
          <button onClick={pick} data-testid="support-checkin-whatsapp"
            className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white rounded-full py-4 text-sm font-bold shadow-lg active:scale-[0.98] transition">
            <MessageCircle className="w-5 h-5" /> {en ? "Contact us on WhatsApp" : "تواصل معنا على الواتس آب"}
          </button>
          <button onClick={onClose} data-testid="support-checkin-ok"
            className="w-full text-sm font-semibold text-neutral-500 py-3">
            {en ? "No, the app is great — all good, thanks!" : "لا، التطبيق ممتاز — كل شيء تمام شكراً"}
          </button>
        </div>
      </div>
    </div>
  );
}
